/**
 * platform#595 — a required ID check that went unmet, as the operator sees it.
 *
 * Some Flows REQUIRE the ID check rather than offer it. The candidate can
 * still finish without it (declining or an unconfirmed result both count as
 * finished), and the backend then marks the case Review Required with a reason.
 * Before this, nothing showed it, so such a case read as cleanly finished.
 *
 * WHAT THIS IS NOT. "Review Required" is rendered as a MARKER beside the
 * existing status pill, never as a new pill or tab: the recruiter-visible
 * status vocabulary is CandidateStatusV1, which is not decided yet (see
 * CLAUDE.md, "The case STATE is deliberately untouched"). The name itself is
 * the one the founders chose for this case, and it is what the backend sends
 * (`status_override: "review_required"`).
 *
 * Declared locally because `dv-api-contract` does not carry `required_idv`
 * yet, the same shim pattern `applicantIdvState.ts` uses.
 */
import { parseApplicantIdvState, type ApplicantIdvState } from "@/lib/applicantIdvState";

export const REQUIRED_IDV_REVIEW_LABEL = "Review Required";

export interface RequiredIdvExceptionEntry {
  action: "retry" | "manual_review";
  at: string;
  /** The operator's own words. Null for a retry. */
  reason: string | null;
}

export interface RequiredIdv {
  required: boolean;
  requirementMet: boolean;
  state: ApplicantIdvState | null;
  /** Only "review_required" is known; anything else is dropped to null. */
  statusOverride: "review_required" | null;
  reasonCode: string | null;
  /** The backend's recruiter-facing sentence, rendered verbatim. */
  reason: string | null;
  /** Case page only; the queue does not send it. */
  exceptionHistory: RequiredIdvExceptionEntry[];
}

function parseHistory(raw: unknown): RequiredIdvExceptionEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: RequiredIdvExceptionEntry[] = [];
  for (const e of raw) {
    if (!e || typeof e !== "object") continue;
    const o = e as Record<string, unknown>;
    if ((o.action !== "retry" && o.action !== "manual_review") || typeof o.at !== "string") continue;
    out.push({ action: o.action, at: o.at, reason: typeof o.reason === "string" ? o.reason : null });
  }
  return out;
}

/**
 * Boundary parser. Null for anything unrecognised, so a payload that changes
 * shape renders nothing rather than a marker nobody can explain.
 */
export function parseRequiredIdv(raw: unknown): RequiredIdv | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.required !== "boolean" || typeof o.requirement_met !== "boolean") return null;
  const override = o.status_override === "review_required" ? "review_required" : null;
  return {
    required: o.required,
    requirementMet: o.requirement_met,
    state: parseApplicantIdvState(o.state),
    statusOverride: override,
    reasonCode: override && typeof o.reason_code === "string" ? o.reason_code : null,
    reason: override && typeof o.reason === "string" ? o.reason : null,
    exceptionHistory: parseHistory(o.exception_history),
  };
}

export function isUnderRequiredIdvReview(r: RequiredIdv | null | undefined): boolean {
  return r?.statusOverride === "review_required";
}

/**
 * The fact, for when something else about the case outranks the marker (the
 * candidate withdrew, the operator already decided, the case went back to the
 * candidate). The requirement stays visibly unmet in every one of those.
 */
export function requiredIdvUnmetLine(r: RequiredIdv | null | undefined): string | null {
  if (!r || !r.required || r.requirementMet || isUnderRequiredIdvReview(r)) return null;
  if (r.state === "declined" || r.state === "failed") {
    return "This role requires an identity check, and it was not completed.";
  }
  // After a retry the case is back with them and the check reads not started
  // or in progress. Without this the card would vanish at exactly the moment
  // the recruiter is waiting on the outcome of their own action.
  if (r.exceptionHistory.some((h) => h.action === "retry")) {
    return "You offered the identity check again. It has not been completed yet.";
  }
  return null;
}

/** Does the case page have anything to show about a required check? */
export function hasRequiredIdvToShow(r: RequiredIdv | null | undefined): boolean {
  return isUnderRequiredIdvReview(r) || requiredIdvUnmetLine(r) !== null;
}
